import { cx } from "@emotion/css";
import Drawer from "@mui/material/Drawer";
import { Pin, PinOff, X } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
	IconButton,
	OutlinedTextBlackButton,
	PrimaryButton,
} from "src/components/Default/LekUI/Button";
import { SwipeableDrawer } from "src/components/Default/LekUI/SwipableDrawer";
import isMobile from "src/helpers/isMobile";

type FiltersDrawerProps = {
	open: boolean;
	onClose: () => void;
	onApply: () => void;
	onClear: () => void;
	activeCount: number;
	// liczba wyników dla wybranych filtrów; undefined = przycisk bez liczby
	resultsCount?: number;
	/** Przypięte (desktop): panel na stałe obok treści zamiast drawera z backdropem. */
	pinned?: boolean;
	/** Bez tego brak przycisku przypinania. */
	onTogglePin?: () => void;
	children: ReactNode;
};

export function FiltersDrawer({
	open,
	onClose,
	onApply,
	onClear,
	activeCount,
	resultsCount,
	pinned,
	onTogglePin,
	children,
}: FiltersDrawerProps) {
	const { t } = useTranslation();
	const isPinned = !!pinned && !isMobile();

	const content = (
		<div
			className={cx("search-filters-drawer", {
				"search-filters-drawer--sheet": isMobile(),
			})}
			data-test-id="search-filters-drawer"
		>
			<div className="search-filters-drawer__header">
				<div className="flex flex-col">
					<span className="size-24">{t("search.filters.title", "Filtry")}</span>
					<span className="size-14-light text-grey">
						{activeCount
							? t("search.filters.active", { count: activeCount })
							: t("search.filters.noneActive", "Brak aktywnych filtrów")}
					</span>
				</div>
				<div className="flex items-center gap-4">
					{onTogglePin && !isMobile() && (
						<IconButton
							onClick={onTogglePin}
							aria-label={
								isPinned
									? t("search.filters.unpin", "Odepnij filtry")
									: t("search.filters.pin", "Przypnij filtry")
							}
							data-test-id="search-filters-pin"
						>
							{isPinned ? (
								<PinOff className="icon-small" />
							) : (
								<Pin className="icon-small" />
							)}
						</IconButton>
					)}
					{!isMobile() && !isPinned && (
						<IconButton
							onClick={onClose}
							aria-label={t("search.filters.close", "Zamknij")}
							data-test-id="search-filters-close"
						>
							<X className="icon-small" />
						</IconButton>
					)}
				</div>
			</div>
			<div className="search-filters-drawer__body">{children}</div>
			<div className="search-filters-drawer__footer">
				<OutlinedTextBlackButton
					className="flex-1"
					onClick={onClear}
					disabled={!activeCount}
					data-test-id="search-filters-clear"
				>
					{t("search.filters.clear", "Wyczyść")}
				</OutlinedTextBlackButton>
				{/* przypięte filtrują od razu — rodzic stosuje każdą zmianę */}
				{!isPinned && (
					<PrimaryButton
						className="search-filters-drawer__submit"
						onClick={onApply}
						data-test-id="search-filters-submit"
					>
						{resultsCount === undefined
							? t("search.filters.showResults", "Pokaż wyniki")
							: t(
									"search.filters.showResultsCount",
									"Pokaż {{count}} wyników",
									{
										count: resultsCount,
									},
								)}
					</PrimaryButton>
				)}
			</div>
		</div>
	);

	if (isPinned) {
		return <aside className="search-filters-pinned">{content}</aside>;
	}

	if (isMobile()) {
		return (
			<SwipeableDrawer open={open} onClose={onClose}>
				{content}
			</SwipeableDrawer>
		);
	}

	return (
		<Drawer
			open={open}
			anchor="right"
			onClose={onClose}
			className="search-filters--drawer"
		>
			{content}
		</Drawer>
	);
}
